import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Shared AudioContext, unlocked on the first user gesture (browser autoplay policy).
let sharedCtx = null;
function getAudioCtx() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!sharedCtx) sharedCtx = new Ctx();
  return sharedCtx;
}
if (typeof window !== "undefined") {
  const unlock = () => {
    const ctx = getAudioCtx();
    if (ctx && ctx.state === "suspended") ctx.resume();
  };
  ["click", "touchstart", "keydown"].forEach((ev) =>
    window.addEventListener(ev, unlock, { once: true, passive: true })
  );
}

const playChime = () => {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume();

    // Note 1
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.frequency.value = 880;
    osc1.type = "sine";
    gain1.gain.setValueAtTime(0.3, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.5);

    // Note 2
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.frequency.value = 1100;
    osc2.type = "sine";
    gain2.gain.setValueAtTime(0.2, ctx.currentTime + 0.2);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
    osc2.start(ctx.currentTime + 0.2);
    osc2.stop(ctx.currentTime + 0.7);
  } catch (e) {
    // audio API unavailable (Safari private mode, HTTPS-only ctx, etc.)
    if (process.env.NODE_ENV === "development") console.debug("notification chime skipped:", e);
  }
};

function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const prevRef = useRef(null); // null until the first poll completes
  const rootRef = useRef(null);

  useEffect(() => {
    if (!user || user.role === "admin") return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/messages/unread-summary");
        if (cancelled) return;
        const n = data.unread || 0;
        if (prevRef.current !== null && n > prevRef.current && document.hasFocus()) playChime();
        prevRef.current = n;
        setCount(n);
        setItems(data.items || []);
      } catch (err) {
        if (err?.response && err.response.status !== 401) {
          console.warn("NotificationBell: unread poll failed", err.response.status);
        }
      }
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  if (!user || user.role === "admin") return null;

  const goToOrder = (orderId) => {
    setOpen(false);
    navigate("/dashboard", { state: { openOrder: orderId } });
  };

  return (
    <div ref={rootRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={count > 0 ? `${count} unread messages` : "Messages"}
        data-testid="notification-bell"
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-md text-[#0f1b2d] hover:bg-[#f1f5f9] transition-colors"
      >
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center leading-none"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-11 z-[70] w-80 max-w-[calc(100vw-1.5rem)] bg-white border border-[#e2e8f0] rounded-lg shadow-xl overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-[#e2e8f0] bg-[#f8fafc] flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Messages</span>
            {count > 0 && <span className="text-xs font-semibold text-red-600">{count} unread</span>}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-[#64748b]" data-testid="notification-dropdown-empty">
              No new messages
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-[#f1f5f9]">
              {items.map((it) => (
                <button
                  key={it.order_id}
                  type="button"
                  onClick={() => goToOrder(it.order_id)}
                  data-testid={`notification-item-${it.order_id}`}
                  className="w-full text-left px-4 py-3 hover:bg-[#f8fafc] transition-colors flex items-start gap-3"
                >
                  <MessageSquare className="w-4 h-4 text-[#2563eb] mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-[#0f1b2d] truncate">{it.order_label}</span>
                      <span className="text-[10px] text-[#94a3b8] flex-shrink-0">{timeLabel(it.last_at)}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-[#475569] line-clamp-2">{it.last_message}</div>
                    <div className="mt-0.5 text-[10px] text-[#94a3b8]">
                      #{it.order_short} · {it.unread} unread
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => { setOpen(false); navigate("/dashboard"); }}
            data-testid="notification-dropdown-footer"
            className="w-full px-4 py-2.5 text-center text-xs font-semibold text-[#2563eb] hover:bg-[#eff6ff] border-t border-[#e2e8f0]"
          >
            Open dashboard →
          </button>
        </div>
      )}
    </div>
  );
};
